/**
 * Conversation Storage
 *
 * Keeps the assistant's conversation for the browser tab (sessionStorage),
 * one entry per signed-in user, so a reload doesn't lose it and the next
 * person to sign in on this tab doesn't inherit it. The adapter itself only
 * keeps messages in memory; AssistantOverlayHost saves via
 * `adapter.subscribe` and restores with `adapter.loadHistory`.
 *
 * Only the real conversation is kept: user/assistant/tool messages. System
 * messages (context notices, truncation notices) are rebuilt by the adapter,
 * empty streaming placeholders are dropped, and a tool call is kept only
 * together with its result — the router rejects a history with an
 * unanswered call.
 */
import type { ChatMessage } from '@neuro/chat-adapter';

const KEY_PREFIX = 'neuro-assistant:conversation:';
export const MAX_SAVED_MESSAGES = 50;

export interface SavedConversation {
  sessionId: string;
  messages: ChatMessage[];
}

export function conversationKey(userKey: string): string {
  return `${KEY_PREFIX}${userKey}`;
}

/** The messages worth restoring, oldest first, capped at `max`. */
export function messagesToSave(messages: readonly ChatMessage[], max = MAX_SAVED_MESSAGES): ChatMessage[] {
  const answered = new Set(
    messages.filter((m) => m.role === 'tool' && m.toolResult).map((m) => m.toolResult!.id),
  );
  const asked = new Set(
    messages.filter((m) => m.role === 'assistant' && m.toolCall).map((m) => m.toolCall!.id),
  );
  const kept = messages.filter((m) => {
    if (m.role === 'user') return !!m.content?.trim();
    if (m.role === 'assistant') {
      if (m.toolCall) return answered.has(m.toolCall.id);
      return !!m.content?.trim();
    }
    if (m.role === 'tool') return !!m.toolResult && asked.has(m.toolResult.id);
    return false;
  });
  const recent = kept.slice(-max);
  // Start on a user turn so the window never opens mid tool exchange.
  const firstUser = recent.findIndex((m) => m.role === 'user');
  return firstUser === -1 ? [] : recent.slice(firstUser);
}

export function saveConversation(
  userKey: string,
  conversation: SavedConversation,
  storage: Storage = sessionStorage,
): void {
  const messages = messagesToSave(conversation.messages);
  try {
    if (messages.length === 0) {
      storage.removeItem(conversationKey(userKey));
      return;
    }
    storage.setItem(conversationKey(userKey), JSON.stringify({ sessionId: conversation.sessionId, messages }));
  } catch {
    // Quota or a disabled storage: the conversation just won't survive a reload.
  }
}

export function loadConversation(userKey: string, storage: Storage = sessionStorage): SavedConversation | null {
  try {
    const raw = storage.getItem(conversationKey(userKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavedConversation>;
    if (typeof parsed.sessionId !== 'string' || !Array.isArray(parsed.messages)) return null;
    const messages = messagesToSave(
      parsed.messages.filter(
        (m): m is ChatMessage => !!m && typeof m === 'object' && typeof m.id === 'string' && typeof m.role === 'string',
      ),
    );
    return messages.length > 0 ? { sessionId: parsed.sessionId, messages } : null;
  } catch {
    return null;
  }
}

export function clearConversation(userKey: string, storage: Storage = sessionStorage): void {
  try {
    storage.removeItem(conversationKey(userKey));
  } catch {
    // Nothing to clear.
  }
}
